/**
 * @fileoverview Loud, admin-only sticky banner shown when the Cal eProcure
 * scraper needs a manual sign-in (so the solicitation/event/award pipelines
 * never fail silently). Mounted above TopNav in MainApp, alongside
 * BackendUnreachableBanner. Also pushes one NotificationBell entry per incident.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AlertOctagon, X } from 'lucide-react';
import { useScraperLoginAlert } from '../../hooks/useScraperLoginAlert';
import './ScraperAuthBanner.css';

export default function ScraperAuthBanner() {
  const { loginAlert, popupAlert, isAdmin } = useScraperLoginAlert();
  const notifiedFp = useRef(null);
  const [dismissed, setDismissed] = useState(() => {
    // Check sessionStorage for dismissed state
    const stored = sessionStorage.getItem('scraperBannerDismissed');
    return stored ? JSON.parse(stored) : null;
  });

  // Login alert takes priority (it pauses everything); popup alert is the
  // softer "detail popups are being blocked, attachments aren't opening" case.
  const activeAlert = loginAlert || popupAlert;
  const isLogin = !!loginAlert;

  // Fire ONE bell notification per incident (keyed by fingerprint), and reset
  // when the alert clears so a future incident re-notifies.
  useEffect(() => {
    if (activeAlert && isAdmin) {
      const fp = activeAlert.fingerprint || activeAlert.kind || 'scraper_alert';
      if (notifiedFp.current !== fp) {
        notifiedFp.current = fp;
        if (typeof window !== 'undefined' && window.addNotification) {
          window.addNotification({
            type: isLogin ? 'error' : 'scraper',
            message: isLogin
              ? 'Cal eProcure scraper needs a manual sign-in — scraping is paused until an admin re-authenticates on the Mac mini.'
              : (activeAlert.current_message || 'Cal eProcure detail popups are being blocked — RFP attachments are not downloading.'),
          });
        }
      }
    } else {
      notifiedFp.current = null;
    }
  }, [activeAlert, isAdmin, isLogin]);

  // Check if this specific alert was dismissed
  const alertFingerprint = activeAlert?.fingerprint || activeAlert?.kind || 'scraper_alert';
  const isDismissed = dismissed === alertFingerprint;

  const handleDismiss = () => {
    setDismissed(alertFingerprint);
    sessionStorage.setItem('scraperBannerDismissed', JSON.stringify(alertFingerprint));
  };

  if (!isAdmin || !activeAlert || isDismissed) return null;

  if (isLogin) {
    const message = loginAlert.current_message || loginAlert.message
      || 'Cal eProcure scraper needs a manual sign-in.';
    const ctx = loginAlert.current_context || loginAlert.context || {};
    const action = ctx.action || 'Run scripts/rfp-interactive-login.js on the Mac mini.';

    /*
     * SAY WHY, BECAUSE THE HEADLINE IS A GUESS.
     *
     * The banner asserted "automated login was rejected" and sent an admin to
     * the Mac mini to sign in by hand. The alert payload carried the real
     * cause the whole time and this component dropped it: on 2026-09-15 the
     * context read `error: "Navigation timeout of 60000 ms exceeded"`.
     *
     * A navigation timeout is not a rejected credential. It is the page never
     * arriving — Cal eProcure slow or down, the Mini's network, or a changed
     * URL — and a manual sign-in fixes none of those. Sending someone to
     * re-authenticate for a timeout costs a trip to another machine and
     * leaves the scraper just as paused.
     *
     * So the underlying error is shown, and when it reads like a timeout
     * rather than an auth failure the banner says the sign-in may not be the
     * fix. It still prints the producer's prescribed action — that call
     * belongs to the prober, which can see things this page cannot.
     */
    const err = typeof ctx.error === 'string' ? ctx.error : null;
    const looksTransport = err && /timeout|ETIMEDOUT|ECONNRESET|ENOTFOUND|net::|navigation/i.test(err);

    // How long it has been firing separates a blip from an outage.
    const age = Number(loginAlert.age_seconds);
    const firingFor = Number.isFinite(age) && age > 0
      ? (age < 3600 ? `${Math.round(age / 60)}m` : `${Math.round(age / 3600)}h`)
      : null;

    return (
      <div className="scraper-auth-banner" role="alert" aria-live="assertive">
        <AlertOctagon size={18} className="scraper-auth-banner__icon" />
        <div className="scraper-auth-banner__text">
          <strong>Cal eProcure sign-in required — scraping paused.</strong>{' '}
          {message}
          {err && (
            <span className="scraper-auth-banner__why">
              Reported cause: <code>{err}</code>
              {firingFor ? ` · firing for ${firingFor}` : ''}
            </span>
          )}
          {looksTransport && (
            <span className="scraper-auth-banner__why">
              That reads as a network or page-load failure, not a rejected
              credential — the sign-in below may not be what fixes it. Check
              that Cal eProcure is reachable from the Mac mini first.
            </span>
          )}
          <span className="scraper-auth-banner__action">{action}</span>
        </div>
        <button
          className="scraper-auth-banner__dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss banner"
          title="Dismiss (will reappear next session)"
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  // Popup-block warning (softer — scraping continues, but attachments are blocked).
  const message = popupAlert.current_message || popupAlert.message
    || 'Cal eProcure detail popups are being blocked — RFP attachments are not downloading.';
  return (
    <div className="scraper-auth-banner scraper-auth-banner--warning" role="alert" aria-live="polite">
      <AlertOctagon size={18} className="scraper-auth-banner__icon" />
      <div className="scraper-auth-banner__text">
        <strong>Cal eProcure detail popups blocked — attachments not downloading.</strong>{' '}
        {message}
        <span className="scraper-auth-banner__action">Check the popup-allow content setting / Chrome version on the Mac mini.</span>
      </div>
      <button
        className="scraper-auth-banner__dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss banner"
        title="Dismiss (will reappear next session)"
      >
        <X size={18} />
      </button>
    </div>
  );
}
